// Copyright 2021 Klabukov Erik.
// SPDX-License-Identifier: GPL-3.0-only

import { HubConnectionBuilder, LogLevel, HttpTransportType } from '@microsoft/signalr'

function WebRtcSignalR(connection, connectCallback) {
  let signalRConnection
  try {
    signalRConnection = new HubConnectionBuilder()
      .withUrl('/ws/webrtc', {
        transport: HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build()
  } catch (e) {
    console.error(e)
  }

  const isData = (session) => !session.audio && !session.video && !session.screen && session.data

  const channelName = connection.channel || 'defaultRoom'

  connection.socket = {
    send: (data) => {
      signalRConnection.invoke('Send', channelName, JSON.stringify(data))
    },
  }

  signalRConnection.on(channelName, (message) => {
    let data = JSON.parse(message)
    if (window.pmDebug)
      console.log('ON Deserialized', data)

    switch (data.eventName) {
      case connection.socketMessageEvent:
        onMessagesCallback(data.data)
        break
      case 'presence':
        data = data.data
        if (data.userid === connection.userid) return
        connection.onUserStatusChanged({
          userid: data.userid,
          status: data.isOnline === true ? 'online' : 'offline',
          extra: connection.peers[data.userid] ? connection.peers[data.userid].extra : {},
        })
        break
      case 'extra-data-updated':
        if (connection.userid === data.data.remoteUserId) return
        if (!connection.peers[data.data.remoteUserId] || !data.data.extra) return
        connection.peers[data.data.remoteUserId].extra = data.data.extra
        connection.onExtraDataUpdated({
          userid: data.data.remoteUserId,
          extra: data.data.extra,
        })
        break
      case 'renegotiate-needed':
        if (connection.userid !== data.data.remoteUserId) return
        if (!connection.peers[data.data.sender]) return
        connection.renegotiate(data.data.sender)
        break
      case 'mute-participant':
        console.warn(
          'User ' +
            (!data.data.all ? data.data.remoteUserId : connection.userid) +
            ' muted by ' +
            data.data.sender
        )
        if (connection.userid !== data.data.remoteUserId && !data.data.all) return
        if (connection.userid === data.data.sender && data.data.all) return
        if (connection.extra.audioMuted) return
        connection.attachStreams.forEach((s) => {
          s.mute('audio')
          connection.extra.audioMuted = true
          connection.updateExtraData()
        })
        if (connection.onMuteForcibly) connection.onMuteForcibly()
        break
      case 'kick-participant':
        console.warn('User ' + data.data.remoteUserId + ' kicked by ' + data.data.sender)
        if (connection.userid !== data.data.remoteUserId) return
        connection.leave()
        if (connection.onKicked) {
          connection.onKicked()
        } else {
          window.location.href = window.location.href + '/ended'
        }
        break
    }
  })
  // eslint-disable-next-line
  signalRConnection.onreconnected((connectionId) => {
    console.log('signalr reconnected. state' + signalRConnection.state)
    signalRConnection.invoke('JoinRoom', channelName).then(() => {
      connection.socket.emit('presence', {
        userid: connection.userid,
        isOnline: true,
      })
    })
  })

  signalRConnection.start().then(() => {
    if (connection.enableLogs) {
      console.info('SignalR connection is opened.')
    }
    signalRConnection.invoke('JoinRoom', channelName).then(() => {
      connection.socket.emit('presence', {
        userid: connection.userid,
        isOnline: true,
      })
      if (connectCallback) connectCallback(connection.socket)
    })
  }).catch((e) => {
    console.error('Error while establishing connection. Error: ' + e.message)
    console.error(JSON.stringify(e, Object.getOwnPropertyNames(e)))
    alert(JSON.stringify(e, Object.getOwnPropertyNames(e)))
  })

  connection.socket.emit = function (eventName, data, callback) {
    if (eventName === 'changed-uuid') return
    if (data.message && data.message.shiftedModerationControl) return

    if (eventName === 'extra-data-updated') {
      data = {
        extra: data,
        remoteUserId: connection.userid,
      }
    }

    connection.socket.send({
      eventName,
      data,
    })

    if (callback) {
      callback = callback || function () {}
      if (eventName === 'open-room' || eventName === 'join-room') {
        callback(true, null)
      } else {
        callback()
      }
    }
  }

  const mPeer = connection.multiPeersHandler

  function onMessagesCallback(message) {
    if (message.remoteUserId !== connection.userid && !message.message.newParticipationRequest) return

    if (
      connection.peers[message.sender] &&
      connection.peers[message.sender].extra !== message.message.extra &&
      message.message.extra
    ) {
      connection.peers[message.sender].extra = message.message.extra
      connection.onExtraDataUpdated({
        userid: message.sender,
        extra: message.message.extra,
      })
    }

    if (message.message.streamSyncNeeded && connection.peers[message.sender]) {
      const stream = connection.streamEvents[message.message.streamid]
      if (!stream || !stream.stream) return

      const action = message.message.action

      if (action === 'ended' || action === 'inactive' || action === 'stream-removed') {
        if (connection.peersBackup[stream.userid]) {
          stream.extra = connection.peersBackup[stream.userid].extra
        }
        connection.onstreamended(stream)
        return
      }

      const type = message.message.type !== 'both' ? message.message.type : null

      if (typeof stream.stream[action] === 'function') {
        stream.stream[action](type)
      }
      return
    }

    if (message.message === 'dropPeerConnection') {
      connection.deletePeer(message.sender)
      return
    }

    if (message.message.allParticipants) {
      if (message.message.allParticipants.indexOf(message.sender) === -1) {
        message.message.allParticipants.push(message.sender)
      }

      message.message.allParticipants.forEach((participant) => {
        mPeer[!connection.peers[participant] ? 'createNewPeer' : 'renegotiatePeer'](
          participant,
          {
            localPeerSdpConstraints: {
              OfferToReceiveAudio: connection.sdpConstraints.mandatory.OfferToReceiveAudio,
              OfferToReceiveVideo: connection.sdpConstraints.mandatory.OfferToReceiveVideo,
            },
            remotePeerSdpConstraints: {
              OfferToReceiveAudio: connection.session.oneway
                ? !!connection.session.audio
                : connection.sdpConstraints.mandatory.OfferToReceiveAudio,
              OfferToReceiveVideo: connection.session.oneway
                ? !!connection.session.video || !!connection.session.screen
                : connection.sdpConstraints.mandatory.OfferToReceiveVideo,
            },
            isOneWay:
              !!connection.session.oneway || connection.direction === 'one-way',
            isDataOnly: isData(connection.session),
          }
        )
      })
      return
    }

    if (message.message.newParticipant) {
      if (message.message.newParticipant === connection.userid) return
      if (connection.peers[message.message.newParticipant]) return

      mPeer.createNewPeer(message.message.newParticipant, message.message.userPreferences || {
        localPeerSdpConstraints: {
          OfferToReceiveAudio: connection.sdpConstraints.mandatory.OfferToReceiveAudio,
          OfferToReceiveVideo: connection.sdpConstraints.mandatory.OfferToReceiveVideo,
        },
        remotePeerSdpConstraints: {
          OfferToReceiveAudio: connection.session.oneway
            ? !!connection.session.audio
            : connection.sdpConstraints.mandatory.OfferToReceiveAudio,
          OfferToReceiveVideo: connection.session.oneway
            ? !!connection.session.video || !!connection.session.screen
            : connection.sdpConstraints.mandatory.OfferToReceiveVideo,
        },
        isOneWay:
          !!connection.session.oneway || connection.direction === 'one-way',
        isDataOnly: isData(connection.session),
      })
      return
    }

    if (message.message.readyForOffer) {
      if (connection.attachStreams.length) {
        connection.waitingForLocalMedia = false
      }

      if (connection.waitingForLocalMedia) {
        setTimeout(() => {
          onMessagesCallback(message)
        }, 1)
        return
      }
    }

    if (message.message.newParticipationRequest && message.sender !== connection.userid) {
      if (connection.peers[message.sender]) {
        connection.deletePeer(message.sender)
      }

      const userPreferences = {
        extra: message.extra || {},
        localPeerSdpConstraints: message.message.remotePeerSdpConstraints || {
          OfferToReceiveAudio: connection.sdpConstraints.mandatory.OfferToReceiveAudio,
          OfferToReceiveVideo: connection.sdpConstraints.mandatory.OfferToReceiveVideo,
        },
        remotePeerSdpConstraints: message.message.localPeerSdpConstraints || {
          OfferToReceiveAudio: connection.session.oneway
            ? !!connection.session.audio
            : connection.sdpConstraints.mandatory.OfferToReceiveAudio,
          OfferToReceiveVideo: connection.session.oneway
            ? !!connection.session.video || !!connection.session.screen
            : connection.sdpConstraints.mandatory.OfferToReceiveVideo,
        },
        isOneWay:
          typeof message.message.isOneWay !== 'undefined'
            ? message.message.isOneWay
            : !!connection.session.oneway || connection.direction === 'one-way',
        isDataOnly:
          typeof message.message.isDataOnly !== 'undefined'
            ? message.message.isDataOnly
            : isData(connection.session),
        dontGetRemoteStream:
          typeof message.message.isOneWay !== 'undefined'
            ? message.message.isOneWay
            : !!connection.session.oneway || connection.direction === 'one-way',
        dontAttachLocalStream: !!message.message.dontGetRemoteStream,
        connectionDescription: message,
        successCallback: () => {},
      }

      connection.onNewParticipant(message.sender, userPreferences)
      return
    }

    if (message.message.changedUUID) {
      if (connection.peers[message.message.oldUUID]) {
        connection.peers[message.message.newUUID] = connection.peers[message.message.oldUUID]
        delete connection.peers[message.message.oldUUID]
      }
    }

    if (message.message.userLeft) {
      mPeer.onUserLeft(message.sender)

      if (message.message.autoCloseEntireSession) {
        connection.leave()
      }

      if (
        connection.DetectRTC.browser.name === 'Firefox' ||
        !connection.peers[message.sender]
      ) {
        connection.onstreamended({ streamid: null, userid: message.sender })
      }

      return
    }

    mPeer.addNegotiatedMessage(message.message, message.sender)
  }
}

export default WebRtcSignalR
